import React, { useEffect, useState } from "react";
import { db } from "../utils/firebase";
import { collection, getDocs } from "firebase/firestore";
import { Box, Typography, List, ListItem, ListItemButton, ListItemText, CircularProgress, Paper, TextField } from "@mui/material";
import { useAuth } from "../context/AuthContext";

export default function AdminUserList({ onUserClick, reloadKey = 0 }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const { user: currentUser } = useAuth();

  useEffect(() => {
    let aborted = false;
    async function fetchUsers() {
      setLoading(true);
      setError("");
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        if (!aborted) setUsers(usersSnap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
      } catch (err) {
        if (!aborted) setError(err?.message || "Unable to load users.");
      } finally {
        if (!aborted) setLoading(false);
      }
    }
    fetchUsers();
    return () => { aborted = true; };
  }, [reloadKey]);

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>All Users</Typography>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  // Exclude the currently logged-in user from the list
  const filteredUsers = users
    .filter(u => (u.role || 'user') !== 'admin')
    .filter(u => !currentUser || u.uid !== currentUser.uid)
    .filter(u => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.uid && u.uid.toLowerCase().includes(q))
      );
    });

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>All Users</Typography>
      <TextField
        placeholder="Search by name or email"
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />
      <List>
        {filteredUsers.length === 0 ? (
          <ListItem><ListItemText primary="No other users found." /></ListItem>
        ) : filteredUsers.map(user => (
          <ListItem key={user.uid} disablePadding>
            <ListItemButton onClick={() => onUserClick(user)}>
              <ListItemText
                primary={user.name || user.email || user.uid}
                secondary={user.email}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}
